import React, { useContext, useEffect, useState, useRef } from 'react';
import AppContext from '../AppContext/AppContext';
import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import Iconify from '../Iconify/Iconify';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import './Panel.scss';
import { Button, TextField, Switch, IconButton, InputAdornment } from '@mui/material';
import 'react-tagsinput/react-tagsinput.css';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import EditorContext from '../EditorContext/EditorContext';
import jsonminify from 'jsonminify';
import { Config } from '../../Config';
import { Block } from '@mui/icons-material';
import { Scrollbars } from 'react-custom-scrollbars-2';

function Panel(props: any) {
	const { state, dispatch } = useContext(AppContext);
	const { projectId, pageId, iframeRef, cssSelector, setCssSelector, exportType, setExportType } = props;
	const [ searchParams ] = useSearchParams();
	const { project, setProject } = useContext(EditorContext);
	const [exportLoading, setExportLoading] = useState(false);
	const [exportText, setExportText] = useState('Export');
	const [exportJson, setExportJson] = useState('');
	const [canCopyTemplate, setCanCopyTemplate] = useState(false);
	const [canCopyFontface, setCanCopyFontface] = useState(false);
	const [copyFontfaceText, setCopyFontfaceText] = useState('Copy CSS @font-face');
	const [reduceDomSize, setReduceDomSize] = useState(false);
	const [reduceDomSizeLevel, setReduceDomSizeLevel] = useState('standard');
	const [mergeElementsEnable, setMergeElementsEnable] = useState(true);
	const [showWebflowAPIToken, setShowWebflowAPIToken] = useState(false);
	const [webflowAPIToken, setWebflowAPIToken] = useState(project?.settings?.webflowAPIToken || '');
	const [webflowUploadAssets, setWebflowUploadAssets] = useState(false);
	const [isOpenAdvanced, setIsOpenAdvanced] = useState(false);
	const [panelHeight, setPanelHeight] = useState(state.license?.licenseId === 'free' ? 'calc(100vh - 290px)' : 'calc(100vh - 197px)');
	let panelRef = useRef<HTMLDivElement | null>(null);
	const [breakpoints, setBreakpoints] = useState<any>(
		{
			desktop: 1025,
			tablet: 1024,
			mobile: 767,
		}
	);
	const [breakpointsElementor, setBreakpointsElementor] = useState<any>(
		{
			desktop: 1025,
			tablet: 1024,
			mobile: 767,
		}
	);
	const [breakpointsElementorFree, setBreakpointsElementorFree] = useState<any>(
		{
			desktop: 1025,
			tablet: 1024,
			mobile: 767,
		}
	);
	const [breakpointsWebflow, setBreakpointsWebflow] = useState<any>(
		{
			desktop: 992,
			tablet: 991,
			mobileLandscape: 767,
			mobile: 478,
		}
	);
	const [breakpointsBricks, setBreakpointsBricks] = useState<any>(
		{
			desktop: 992,
			tablet: 991,
			mobileLandscape: 767,
			mobile: 478,
		}
	);
	const [breakpointsBreakdance, setBreakpointsBreakdance] = useState<any>(
		{
			desktop: 1120,
			tabletLandscape: 1119,
			tablet: 1023,
			phoneLandscape: 767,
			mobile: 479
		}
	);
	const [breakpointsGutenberg, setBreakpointsGutenberg] = useState<any>(
		{
			desktop: 1120,
			tablet: 780,
			mobile: 360
		}
	);
	const [breakpointsDivi, setBreakpointsDivi] = useState<any>(
		{
			desktop: 1120,
			tablet: 780,
			mobile: 360
		}
	);
	const [builder, setBuilder] = useState('elementorFree');
	const [copyFontface, setCopyFontface] = useState('export');
	const [fontFace, setFontFace] = useState<any>(null);
	const navigate = useNavigate();
	const [jsonString, setJsonString] = useState('');
	const [error, setError] = useState<any>(null);
	const [upgrade, setUpgrade] = useState(state.license?.licenseId === 'free');
	const [copyClipboard, setCopyClipboard] = useState(false);
	const [uploadDocURL, setUploadDocURL] = useState<string>('https://softlite.io/docs/uibrainx/elementor-use-images-or-icons-instead-of-source-links/');
	const [mergeElementsDocURL, setMergeElementsDocURL] = useState<string>('https://softlite.io/docs/uibrainx/merge-elements/');

	const handleExport = async () => {
		if (cssSelector.trim() === '' && exportType !== 'entirePage' && builder !== 'figma') {
			setError('Please enter a CSS selector');
			return;
		} else {
			setError(null);
		}

		const iframe = iframeRef.current;
		const iframeDocument = iframe?.contentDocument;

		if (exportType !== 'entirePage') {
			try {
				const element = iframeDocument?.querySelector(cssSelector);
				if (!element) {
					setError('Invalid CSS selector');
					return;
				}
			} catch (error) {
				setError('Invalid CSS selector');
				return;
			}
		}

		try {
			setExportLoading(true);
			setCanCopyTemplate(false);
			setCanCopyFontface(false);

			const option = {
				method: 'POST',
				url: '/api/v1/export/',
				headers: {
					Authorization: `Bearer ${localStorage.getItem('token')}`,
				},
				data: {
					pageId: pageId,
					exportType: exportType,
					selector: cssSelector,
					breakpoints: breakpoints,
					builder: builder,
					copyFontface: copyFontface,
					reduceDomSize: reduceDomSize ? reduceDomSizeLevel : null,
					webflowAPIToken: webflowAPIToken,
					webflowUploadAssets: webflowUploadAssets,
					mergeElementsEnable: mergeElementsEnable,
				},
			};

			const res = await axios(option);
			const data = res.data.template;
			let copyClipboardData = JSON.stringify(data).length < 1000000 ? true : false;
			if (builder === 'divi') {
				copyClipboardData = false;
			}
			setCopyClipboard(copyClipboardData);
			setExportJson(data);
			setFontFace(res.data.fontFace);

			if (['webflow', 'figma'].includes(builder)) {
				setCanCopyTemplate(true);
			} else if (['elementor', 'elementorFree', 'bricks', 'breakdance', 'gutenberg', 'divi'].includes(builder)) {
				if (copyClipboardData || ['breakdance', 'gutenberg'].includes(builder)) {
					setCanCopyTemplate(true);
					copyJsonToClipboard(data);
				} else {
					if (builder === 'bricks') {
						data.global_classes = data.globalClasses;
						delete data.globalClasses;
					}
					
					let title = data?.title;
					if(builder === 'divi'){
						delete data?.title;
					}
					const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					link.setAttribute('download', textToSlug(title) + '.json');
					document.body.appendChild(link);
					link.click();
				}
			}

			if (copyFontface === 'copy') {
				setCanCopyFontface(true);
			}

			setExportLoading(false);
			setError(null);
		} catch (error: any) {
			if (error.response.status === 409) {
				setUpgrade(true);
				setError(error.response.data.error);
			} else {
				setError('Processing error, please send a support request to the support team');
			}
		} finally {
			setExportLoading(false);
		}
	}

	const handleCopyWebflow = () => {
		document.execCommand('copy');
	};

	document.addEventListener("copy", function (event) {
		event.preventDefault();
		event?.clipboardData?.setData('text/html', exportJson);
		event?.clipboardData?.setData("application/json", JSON.stringify(exportJson));

		setExportText('Copied');
		setTimeout(() => {
			setExportText('Export');
		}, 3000);
	});

	const copyJsonToClipboard = (data: any) => {
		if (data) {
			let jsonString = '';

			switch (builder) {
				case 'elementor':
				case 'elementorFree':

					jsonString = JSON.stringify({ type: 'elementor', siteurl: 'https://elementor.com/wp-json/', elements: data.content }, null, 2);

					break;

				case 'bricks':

					jsonString = JSON.stringify({
						content: data.content,
						source: 'bricksCopiedElements',
						sourceUrl: '',
						version: '1.9.2',
						globalClasses: data.globalClasses,
						globalElements: []
					}, null, 2);

					break;
				case 'breakdance':
					jsonString = JSON.stringify(data, null, 2);
					break;
			}

			const jsonStringMinified = ['divi', 'gutenberg', 'figma'].includes(builder) ? data : JSON.stringify(JSON.parse(jsonString));

			navigator.clipboard.writeText(jsonStringMinified)
				.then(() => {
					setExportText('Copied');
					setTimeout(() => {
						setExportText('Export');
					}, 3000);
				})
				.catch((error) => {
				});
		}
	};

	useEffect(() => {
		setCanCopyFontface(false);
	}, [copyFontface, exportType, builder, cssSelector]);

	const handleCopyFontface = () => {
		navigator.clipboard.writeText('<style>' + fontFace + '</style>')
			.then(() => {
				setCopyFontfaceText('Copied');
				setTimeout(() => {
					setCopyFontfaceText('Copy CSS @font-face');
				}, 3000);
			})
			.catch((error) => {
				console.error('Error copying data to clipboard:', error);
			});
	};

	useEffect(() => {
		setCanCopyTemplate(false);
	}, [exportType, builder, cssSelector]);

	useEffect(() => {
		panelRef.current?.addEventListener('mouseover', (e: any) => {
			const iframe = iframeRef.current;
			const iframeWindow = iframe?.contentWindow;
			if (typeof iframeWindow.uibrainxSelectorRect === "function") {
				iframeWindow.uibrainxSelectorRect({ hide: true });
			}
		});
	}, [iframeRef]);

	useEffect(() => {
		const fetchSites = async () => {
			try {
				const option = {
					method: 'PUT',
					url: '/api/v1/webflow/',
					headers: {
						Authorization: `Bearer ${localStorage.getItem('token')}`,
					},
					data: {
						projectId: projectId,
						webflowAPIToken: webflowAPIToken,
					},
				};

				const res = await axios(option);

			} catch (error: any) {
				console.error('Error fetching sites:', error.response ? error.response.data : error.message);
			}
		};

		if (webflowAPIToken) {
			fetchSites();
		}

	}, [webflowAPIToken]);

	useEffect(() => {
		if (project?.settings?.webflowAPIToken) {
			setWebflowAPIToken(project?.settings?.webflowAPIToken);
		}
	}, [project]);

	useEffect(() => {
		switch (builder) {
			case 'elementorFree':
				setUploadDocURL('https://softlite.io/docs/uibrainx/elementor-use-images-or-icons-instead-of-source-links/');
				break;
			case 'webflow':
				setUploadDocURL('https://softlite.io/docs/uibrainx/webflow-use-images-or-icons-instead-of-source-links/');
				break;
			case 'bricks':
			case 'breakdance':
			case 'divi':
				setUploadDocURL('https://softlite.io/docs/uibrainx/use-images-or-icons-instead-of-source-links/');
				break;
		}
	}, [builder]);

	const renderThumb = () => {
		return (
			<div style={{ background: '#212426', borderRadius: '100px' }} />
		);
	}

	return (
		<div className="panel" ref={panelRef}>
			<div className="panel__inner">
				<div className="panel__close">
					<IconButton onClick={() => { props.setOpenModal(false) }}>
						<Iconify icon="material-symbols:close" />
					</IconButton>
				</div>
				
				<div className="controls">
					<div className="controls__body">
						{builder !== 'figma' && (
							<>
								<div className="controls__field-group controls__field-group--label-block">
									<label htmlFor="exportType">Export Type</label>
									<Select
										id="exportType"
										name='exportType'
										value={exportType}
										onChange={(e) => {
											setExportType(e.target.value);
										}}
										IconComponent={() => (
											<div className="control-select-arrow">
												<Iconify icon="material-symbols:keyboard-arrow-down" />
											</div>
										)}
									>
										<MenuItem value="entirePage" style={{fontSize: '12px'}}>Entire Page</MenuItem>
										<MenuItem value="selector" style={{fontSize: '12px'}}>Selector</MenuItem>
									</Select>
								</div>
								{exportType === 'selector' && (
									<div className="controls__field-group controls__field-group--label-block">
										<label className='label-flex' htmlFor="selector">
											CSS Selector (click on the element)
										</label>
										<TextField
											id="selector"
											name='selector'
											value={cssSelector}
											onChange={(e) => {
												setExportType('selector');
												setCssSelector(e.target.value);
											}}
										/>
									</div>
								)}
							</>
						)}

						<div className="controls__field-group controls__field-group--label-block">
							<label htmlFor="builder">Page Builder</label>
							<Select
								id="builder"
								name='builder'
								value={builder}
								onChange={(e) => {
									let pageBuilder = e.target.value;
									setBuilder(pageBuilder);
									pageBuilder = pageBuilder.charAt(0).toUpperCase() + pageBuilder.slice(1);
									pageBuilder = pageBuilder.split('_')[0];
									setBreakpoints(pageBuilder !== 'Figma' ? eval(`breakpoints${pageBuilder}`) : '');
								}}
								IconComponent={() => (
									<div className="control-select-arrow">
										<Iconify icon="material-symbols:keyboard-arrow-down" />
									</div>
								)}
							>
								<MenuItem value="elementorFree" style={{ fontSize: '12px' }}>Elementor{state.license.licenseId === 'free' ? ' (Paid)' : ' (3.10 or higher + Flex Container)'}</MenuItem>
								<MenuItem value="gutenberg" style={{ fontSize: '12px' }}>Gutenberg</MenuItem>
								<MenuItem value="webflow" style={{ fontSize: '12px' }}>Webflow</MenuItem>
								<MenuItem value="bricks" style={{ fontSize: '12px' }}>Bricks{state.license.licenseId === 'free' ? ' (Paid)' : ''}</MenuItem>
								<MenuItem value="breakdance" style={{ fontSize: '12px' }}>Breakdance{state.license.licenseId === 'free' ? ' (Paid)' : ''}</MenuItem>
								<MenuItem value="divi" style={{ fontSize: '12px' }}>Divi 5{state.license.licenseId === 'free' ? ' (Paid)' : ''}</MenuItem>
								<MenuItem value="figma" style={{ fontSize: '12px' }}>Figma{state.license.licenseId === 'free' ? ' (Paid)' : ''}</MenuItem>
							</Select>
						</div>

						{['elementorFree', 'elementor', 'gutenberg', 'bricks', 'breakdance', 'divi'].includes(builder) && (
							<div className='require-notices'>
								<strong style={{ display: "block", marginBottom: "3px" }}>Requirements:</strong>
								<ul>
									<li>
										<a href={'https://softlite.io/updater/softlite-io-integration.zip?v=' + Config.PLUGIN_VERSION} download>
											Click here to Download & Install Softlite.io Integration Plugin version {Config.PLUGIN_VERSION} <span className="moveUpDown" style={{ display: 'inline-block' }}><Iconify icon="bxs:download" width={14} height={14} style={{ transform: 'translateY(3px)' }} /></span>
										</a>
									</li>
									{!['gutenberg', 'bricks', 'breakdance', 'divi'].includes(builder) && (
										<>
											<li>Flexbox Container installation is mandatory.</li>
											<li>Paste data: Right-click Elementor Editor &gt; Choose "Paste from other site" &gt; Ctrl/Cmd+V.</li>
										</>
									)}
								</ul>
							</div>
						)}

						{['elementorFree', 'elementor'].includes(builder) && (
							<div className="controls__field-group">
								<label htmlFor="mergeElementsEnable">
									<span>Merge Elements</span>
								</label>
								<Switch
									checked={mergeElementsEnable ? true : false}
									onChange={(e) => {
										setMergeElementsEnable(e.target.checked);
									}}
									id='mergeElementsEnable'
								/>
								{mergeElementsEnable && (
									<div className="full-width c-gray-dark">
										Disable this function for layout issues. <a href={mergeElementsDocURL} target="_blank">Learn more →</a>
									</div>
								)}
							</div>
						)}

						{builder !== 'figma' && (
							<div className="controls__field-group">
								<a href={uploadDocURL} target="_blank" className='full-width'>Automatically upload images/icons/fonts to your server. Learn more →</a>
							</div>
						)}

						{builder === 'webflow' && (
							<>
								<div className="controls__field-group">
									<label htmlFor="webflowUploadAssets">
										<span>Upload images to assets</span>
									</label>
									<Switch
										checked={webflowUploadAssets ? true : false}
										onChange={(e) => {
											setWebflowUploadAssets(e.target.checked);
										}}
										id='webflowUploadAssets'
									/>
								</div>
								{webflowUploadAssets && (
									<div className="controls__field-group controls__field-group--label-block">
										<label htmlFor="webflowAPIToken">
											Webflow API Token
										</label>
										<TextField
											id="webflowAPIToken"
											name='webflowAPIToken'
											value={webflowAPIToken}
											onChange={(e) => {
												setWebflowAPIToken(e.target.value);
											}}
											type={showWebflowAPIToken ? 'text' : 'password'}
											className='webflowAPIToken'
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<IconButton onClick={() => setShowWebflowAPIToken(!showWebflowAPIToken)} edge="end">
															<Iconify icon={showWebflowAPIToken ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
									</div>
								)}
							</>
						)}
						
						<div className="controls__advanced">
							<div className="controls__advanced-header" onClick={() => setIsOpenAdvanced(!isOpenAdvanced)}>
								<span>Advanced settings</span>
								{!isOpenAdvanced && (
									<Iconify icon="akar-icons:chevron-down" />
								)}
								{isOpenAdvanced && (
									<Iconify icon="akar-icons:chevron-up" />
								)}
							</div>
							{isOpenAdvanced && (
								<div className="controls__advanced-content">

									{breakpoints.tabletLandscape && (
										<div className="controls__field-group">
											<label className='label-flex' htmlFor="breakpointsMobile">
												Tablet Landscape (px)
											</label>
											<TextField
												id="breakpointsTabletLandscape"
												name='breakpointsTabletLandscape'
												value={breakpoints.tabletLandscape}
												type='number'
												onChange={(e) => {
													setBreakpoints({ ...breakpoints, tabletLandscape: parseInt(e.target.value) });
													eval(`setBreakpoints${builder.charAt(0).toUpperCase() + builder.slice(1)}({...breakpoints, tabletLandscape: parseInt(e.target.value)})`);
												}}
											/>
										</div>
									)}

									{breakpoints.tablet && (
										<div className="controls__field-group">
											<label className='label-flex' htmlFor="breakpointsTablet">
												Tablet Portrait (px)
											</label>
											<TextField
												id="breakpointsTablet"
												name='breakpointsTablet'
												value={breakpoints.tablet}
												type='number'
												onChange={(e) => {
													setBreakpoints({ ...breakpoints, desktop: parseInt(e.target.value) + 1, tablet: parseInt(e.target.value) });
													eval(`setBreakpoints${builder.charAt(0).toUpperCase() + builder.slice(1)}({...breakpoints, tablet: parseInt(e.target.value)})`);
												}}
											/>
										</div>
									)}

									{breakpoints.mobileLandscape && (
										<div className="controls__field-group">
											<label className='label-flex' htmlFor="breakpointsMobileLandscape">
												Mobile Landscape (px)
											</label>
											<TextField
												id="breakpointsMobileLandscape"
												name='breakpointsMobileLandscape'
												value={breakpoints.mobileLandscape}
												type='number'
												onChange={(e) => {
													if (breakpoints.mobileLandscape) {
														setBreakpoints({ ...breakpoints, mobileLandscape: parseInt(e.target.value) });
														eval(`setBreakpoints${builder.charAt(0).toUpperCase() + builder.slice(1)}({...breakpoints, mobileLandscape: parseInt(e.target.value)})`);
													}
												}}
											/>
										</div>
									)}

									{breakpoints.phoneLandscape && (
										<div className="controls__field-group">
											<label className='label-flex' htmlFor="breakpointsPhoneLandscape">
												Phone Landscape (px)
											</label>
											<TextField
												id="breakpointsPhoneLandscape"
												name='breakpointsPhoneLandscape'
												value={breakpoints.phoneLandscape}
												type='number'
												onChange={(e) => {
													setBreakpoints({ ...breakpoints, phoneLandscape: parseInt(e.target.value) });
													eval(`setBreakpoints${builder.charAt(0).toUpperCase() + builder.slice(1)}({...breakpoints, phoneLandscape: parseInt(e.target.value)})`);
												}}
											/>
										</div>
									)}

									{breakpoints.phonePortrait && (
										<div className="controls__field-group">
											<label className='label-flex' htmlFor="breakpointsMobile">
												Phone Portrait (px)
											</label>
											<TextField
												id="breakpointsPhonePortrait"
												name='breakpointsPhonePortrait'
												value={breakpoints.phonePortrait}
												type='number'
												onChange={(e) => {
													setBreakpoints({ ...breakpoints, phonePortrait: parseInt(e.target.value) });
													eval(`setBreakpoints${builder.charAt(0).toUpperCase() + builder.slice(1)}({...breakpoints, phonePortrait: parseInt(e.target.value)})`);
												}}
											/>
										</div>
									)}

									{breakpoints.mobile && (
										<div className="controls__field-group">
											<label className='label-flex' htmlFor="breakpointsMobile">
												{builder === 'breakdance' ? 'Phone' : 'Mobile'} Portrait (px)
											</label>
											<TextField
												id="breakpointsMobile"
												name='breakpointsMobile'
												value={breakpoints.mobile}
												type='number'
												onChange={(e) => {
													setBreakpoints({ ...breakpoints, mobile: parseInt(e.target.value) });
													eval(`setBreakpoints${builder.charAt(0).toUpperCase() + builder.slice(1)}({...breakpoints, mobile: parseInt(e.target.value)})`);
												}}
											/>
										</div>
									)}

									<div className="controls__field-group controls__field-group--label-block">
										<div className="flex flex-space-between align-center margin-bottom-10">
											<label className='label-flex' htmlFor="copyFontface">
												CSS @font-face
											</label>
											{copyFontface === 'copy' && canCopyFontface && (
												<div className="copy" onClick={() => handleCopyFontface()}>{copyFontfaceText}</div>
											)}
										</div>
										<Select
											id="copyFontface"
											name='copyFontface'
											value={copyFontface}
											onChange={(e) => {
												let copyFontface = e.target.value;
												setCopyFontface(copyFontface);
											}}
											IconComponent={() => (
												<div className="control-select-arrow">
													<Iconify icon="material-symbols:keyboard-arrow-down" />
												</div>
											)}
										>
											<MenuItem value="export" style={{ fontSize: '12px' }}>Add it to the export data</MenuItem>
											<MenuItem value="copy" style={{ fontSize: '12px' }}>Copy it to your site yourself</MenuItem>
										</Select>
									</div>
								</div>
							)}
						</div>

						{builder === 'figma' && (
							<div className="controls__field-group">
								If you're experiencing issues with the export, please use the html.to.design Figma plugin to convert the page using this URL: {props.pageURL}
							</div>
						)}

					</div>
				</div>
				
				<div className="panel__footer">
					<div className="panel__footer-tools">
						<div className="panel__footer-right flex align-center">
							{/* {['elementor', 'webflow'].includes(builder) && (
								<LoadingButton loading={exportLoading} onClick={() => handleExport()} variant="contained">{exportText}</LoadingButton>
							)} */}
							{['elementor', 'elementorFree', 'webflow', 'bricks', 'breakdance', 'gutenberg', 'figma', 'divi'].includes(builder) && (
								<LoadingButton loading={exportLoading} onClick={() => handleExport()} variant="contained" className={upgrade && !['webflow', 'gutenberg'].includes(builder) ? 'export-button export-button--disabled' : 'export-button'}>{exportText}</LoadingButton>
							)}
							{(['webflow', 'figma'].includes(builder) && !exportLoading && canCopyTemplate) && (
								<Button onClick={() => handleCopyWebflow()} variant="contained">Copy to clipboard</Button>
							)}
							{(['elementor', 'elementorFree', 'bricks', 'breakdance', 'gutenberg', 'divi'].includes(builder) && !exportLoading && canCopyTemplate && (copyClipboard || ['breakdance', 'gutenberg'].includes(builder))) && (
								<Button onClick={() => copyJsonToClipboard(exportJson)} variant="contained">Copy to clipboard</Button>
							)}
						</div>
					</div>

					{error && (
						<div className="panel__notice">
							<div className="panel__notice-icon">
								<Iconify icon="ph:info" />
							</div>
							<div className="panel__notice-body">
								{error}
							</div>
						</div>
					)}
					{upgrade && (
						<>
							<div className="panel__notice">
								<div className="panel__notice-icon">
									<Iconify icon="ph:info" />
								</div>
								<div className="panel__notice-body">
									The responsive is not available in the Free plan
								</div>
							</div>
							<div className="panel__upgrade">
								<a href={'https://softlite.io/pricing/uibrainx/?email=' + state.user.email + '&first_name=' + state.user.first_name + '&last_name=' + state.user.last_name} target="_blank">
									<LoadingButton fullWidth variant="contained">Upgrade</LoadingButton>
								</a>
							</div>
						</>
					)}
				</div>
				
			</div>
		</div>
	);
}

const textToSlug = (text: string) => {
	return text
		.toLowerCase()
		.replace(/ /g, '-')
		.replace(/[^\w-]+/g, '');
};

export default Panel;
